import { useRouterState } from "@tanstack/react-router";
import {
  UpdateFeedLastLocation,
  updateFeedLastLocationQueryString,
} from "@/data-access/user/queries/me-queries";
import { useDebouncedPersistWithKeepaliveFlush } from "@/hooks/useDebouncedPersistWithKeepaliveFlush";
import { formatLastVisitedLocation } from "@/lib/auth/authLoginUtils";
import {
  type FeedSurface,
  parseFeedLastLocationContext,
  shouldPersistFeedLastLocation,
} from "@/lib/auth/feedLastLocationUtils";
import { client } from "@/lib/urqlProvider";
import { mapMeFeedLastLocationToFeedLastLocation } from "@/store/auth.helpers";
import useAuth from "@/store/auth.slice";
import type { FeedLastLocation } from "@/types/user/userInfo";

interface PendingFeedLastLocation {
  feedId: string;
  surface: FeedSurface;
  location: string;
}

const pendingFeedLastLocationKey = ({
  feedId,
  surface,
  location,
}: PendingFeedLastLocation): string => `${feedId}:${surface}:${location}`;

const upsertFeedLastLocation = (
  locations: FeedLastLocation[] | undefined,
  entry: FeedLastLocation,
): FeedLastLocation[] => {
  const rest = (locations ?? []).filter(
    (item) =>
      !(item.feed_id === entry.feed_id && item.surface === entry.surface),
  );
  return [...rest, entry];
};

const findCachedFeedLastLocation = (
  feedId: string,
  surface: FeedSurface,
): FeedLastLocation | undefined =>
  useAuth
    .getState()
    .user.feed_last_locations?.find(
      (entry) => entry.feed_id === feedId && entry.surface === surface,
    );

const patchFeedLastLocationInAuth = (entry: FeedLastLocation): void => {
  const { user } = useAuth.getState();
  const existing = findCachedFeedLastLocation(entry.feed_id, entry.surface);
  if (existing?.location === entry.location) return;

  useAuth.setState({
    user: {
      ...user,
      feed_last_locations: upsertFeedLastLocation(
        user.feed_last_locations,
        entry,
      ),
    },
  });
};

/**
 * Watches Analysis and Discovery route changes and persists per-feed pathname + search.
 * Flushes pending writes when the tab is hidden. Runs alongside {@link useLastVisitedLocationSync}.
 */
export const useFeedLastLocationSync = () => {
  const loggedIn = useAuth((state) => state.loggedIn);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });

  useDebouncedPersistWithKeepaliveFlush({
    loggedIn,
    scheduleDeps: [pathname, searchStr],
    resolvePending: () => {
      if (!shouldPersistFeedLastLocation(pathname)) return null;

      const context = parseFeedLastLocationContext(pathname);
      if (!context) return null;

      return {
        feedId: context.feedId,
        surface: context.surface,
        location: formatLastVisitedLocation(pathname, searchStr),
      };
    },
    persistKey: pendingFeedLastLocationKey,
    isAlreadyCached: (pending) =>
      findCachedFeedLastLocation(pending.feedId, pending.surface)?.location ===
      pending.location,
    persist: async (pending) => {
      const result = await client
        .mutation(UpdateFeedLastLocation, { input: pending })
        .toPromise();

      if (result.error) {
        console.warn("Failed to persist feed last location:", result.error);
        throw result.error;
      }

      const updated = result.data?.updateFeedLastLocation;
      if (!updated) return;

      patchFeedLastLocationInAuth(
        mapMeFeedLastLocationToFeedLastLocation(updated),
      );
    },
    flushQueryString: updateFeedLastLocationQueryString,
    flushVariables: (pending) => ({ input: pending }),
    onKeepaliveFlushSuccess: (pending) => {
      patchFeedLastLocationInAuth({
        feed_id: pending.feedId,
        surface: pending.surface,
        location: pending.location,
        updated_at: new Date().toISOString(),
      });
    },
    flushErrorMessage: "Failed to flush feed last location:",
  });
};
